/**
 * PermitInspectionPanel — Phase 1E
 * ─────────────────────────────────────────────────────────────────────
 * Structured inspection-lifecycle surface for the Permit Detail drawer.
 *
 * - Header: lifecycle badge (Not Started / Active / Complete)
 * - Body: per-inspection rows w/ pass/fail entry + failure metadata
 * - Footer: derived progress + readiness badge
 * - Add-inspection form
 *
 * Pure presentation + form glue. All mutations route through the
 * `inspectionLifecycle.ts` helpers via the supplied callbacks.
 */
import { useMemo, useState } from "react";
import { PermissionGate } from "@/components/PermissionGate";
import type { Job } from "@/data/types";
import {
  resolveInspectionReadiness,
  inspectionInputSchema,
  inspectionResultInputSchema,
  SUGGESTED_INSPECTION_TYPES,
  type InspectionInput,
  type InspectionResultInput,
  type PermitInspection,
} from "@/data/permitting/inspectionLifecycle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  CheckCircle2, XCircle, Plus, ClipboardCheck, AlertTriangle,
  Calendar, User as UserIcon, RotateCcw,
} from "lucide-react";
import { formatShortDate } from "@/lib/dateFormat";

interface Props {
  job: Job;
  onAddInspection: (jobId: string, input: InspectionInput) => void;
  onRecordResult: (jobId: string, inspectionId: string, input: InspectionResultInput) => void;
  onClearResult: (jobId: string, inspectionId: string) => void;
}

// ── Add-inspection sub-form ────────────────────────────────────────

function AddInspectionForm({
  onSubmit,
  onCancel,
}: {
  onSubmit: (input: InspectionInput) => void;
  onCancel: () => void;
}) {
  const [type, setType] = useState("");
  const [scheduled, setScheduled] = useState("");
  const [owner, setOwner] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);

  const submit = () => {
    setError(null);
    const isoScheduled = scheduled
      ? new Date(`${scheduled}T12:00:00Z`).toISOString()
      : undefined;
    const parsed = inspectionInputSchema.safeParse({
      inspectionType: type,
      scheduledDate: isoScheduled,
      assignedOwner: owner || undefined,
      notes: notes || undefined,
    });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? "Invalid inspection");
      return;
    }
    onSubmit(parsed.data);
    setType(""); setScheduled(""); setOwner(""); setNotes("");
  };

  return (
    <div className="rounded-md border bg-muted/20 p-3 space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label className="text-[11px]">Inspection type *</Label>
          <Input
            list="inspection-type-suggestions"
            value={type}
            onChange={(e) => setType(e.target.value)}
            placeholder="Rough Plumbing, Final Building…"
            className="h-8 text-xs mt-1"
          />
          <datalist id="inspection-type-suggestions">
            {SUGGESTED_INSPECTION_TYPES.map((t) => (
              <option key={t} value={t} />
            ))}
          </datalist>
        </div>
        <div>
          <Label className="text-[11px]">Scheduled date</Label>
          <Input
            type="date"
            value={scheduled}
            onChange={(e) => setScheduled(e.target.value)}
            className="h-8 text-xs mt-1"
          />
        </div>
        <div className="md:col-span-2">
          <Label className="text-[11px]">Assigned owner</Label>
          <Input
            value={owner}
            onChange={(e) => setOwner(e.target.value)}
            placeholder="Coordinator on the hook for this inspection"
            className="h-8 text-xs mt-1"
          />
        </div>
        <div className="md:col-span-2">
          <Label className="text-[11px]">Notes</Label>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className="text-xs mt-1 resize-none"
          />
        </div>
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onCancel} className="h-7 text-xs">Cancel</Button>
        <PermissionGate permission="jobs_approve">
          <Button size="sm" onClick={submit} className="h-7 text-xs">Add inspection</Button>
        </PermissionGate>
      </div>
    </div>
  );
}

// ── Inline pass/fail entry (per-row) ───────────────────────────────

function ResultEntry({
  onSubmit,
  onCancel,
}: {
  onSubmit: (input: InspectionResultInput) => void;
  onCancel: () => void;
}) {
  const [mode, setMode] = useState<"passed" | "failed" | null>(null);
  const [reason, setReason] = useState("");
  const [correction, setCorrection] = useState(true);
  const [reinspect, setReinspect] = useState(true);
  const [error, setError] = useState<string | null>(null);

  if (mode === null) {
    return (
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          className="h-7 text-xs gap-1 text-success border-success/40 hover:bg-success/10"
          onClick={() => setMode("passed")}
        >
          <CheckCircle2 className="h-3 w-3" /> Pass
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="h-7 text-xs gap-1 text-destructive border-destructive/40 hover:bg-destructive/10"
          onClick={() => setMode("failed")}
        >
          <XCircle className="h-3 w-3" /> Fail
        </Button>
        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    );
  }

  const submit = () => {
    setError(null);
    const parsed = inspectionResultInputSchema.safeParse({
      result: mode,
      failureReason: mode === "failed" ? reason : undefined,
      correctionRequired: mode === "failed" ? correction : undefined,
      reinspectionRequired: mode === "failed" ? reinspect : undefined,
    });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? "Invalid");
      return;
    }
    onSubmit(parsed.data);
  };

  if (mode === "passed") {
    return (
      <div className="rounded-md border bg-success/5 border-success/30 p-2 flex items-center justify-between gap-2">
        <span className="text-xs text-success font-medium flex items-center gap-1">
          <CheckCircle2 className="h-3 w-3" /> Mark this inspection passed?
        </span>
        <div className="flex gap-2">
          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={onCancel}>Cancel</Button>
          <PermissionGate permission="jobs_approve">
            <Button size="sm" className="h-7 text-xs" onClick={submit}>Confirm pass</Button>
          </PermissionGate>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-md border bg-destructive/5 border-destructive/30 p-3 space-y-3">
      <div>
        <Label className="text-[11px]">Failure reason *</Label>
        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={2}
          placeholder="What did the inspector flag?"
          className="text-xs mt-1 resize-none"
        />
      </div>
      <div className="flex flex-wrap items-center gap-4">
        <label className="flex items-center gap-2 text-xs cursor-pointer">
          <Checkbox
            checked={correction}
            onCheckedChange={(v) => setCorrection(v === true)}
          />
          Correction required
        </label>
        <label className="flex items-center gap-2 text-xs cursor-pointer">
          <Checkbox
            checked={reinspect}
            onCheckedChange={(v) => setReinspect(v === true)}
          />
          Reinspection required
        </label>
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={onCancel}>Cancel</Button>
        <PermissionGate permission="jobs_approve">
          <Button size="sm" className="h-7 text-xs" onClick={submit}>Confirm fail</Button>
        </PermissionGate>
      </div>
    </div>
  );
}

// ── Per-inspection row ─────────────────────────────────────────────

function InspectionRow({
  jobId,
  inspection,
  onRecord,
  onClear,
}: {
  jobId: string;
  inspection: PermitInspection;
  onRecord: (jobId: string, inspectionId: string, input: InspectionResultInput) => void;
  onClear: (jobId: string, inspectionId: string) => void;
}) {
  const [editing, setEditing] = useState(false);

  const tone =
    inspection.result === "passed" ? "bg-success/[0.04] border-success/30" :
    inspection.result === "failed" ? "bg-destructive/[0.04] border-destructive/40" :
    "bg-card border-border";

  return (
    <div className={`rounded-md border p-3 ${tone}`}>
      <div className="flex items-start gap-3">
        <div className="mt-0.5 shrink-0">
          {inspection.result === "passed" ? (
            <CheckCircle2 className="h-5 w-5 text-success" />
          ) : inspection.result === "failed" ? (
            <XCircle className="h-5 w-5 text-destructive" />
          ) : (
            <ClipboardCheck className="h-5 w-5 text-muted-foreground" />
          )}
        </div>
        <div className="flex-1 min-w-0 space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-semibold text-foreground">{inspection.inspectionType}</span>
            {inspection.result === "passed" && (
              <Badge variant="outline" className="bg-success/15 text-success border-success/30 text-[10px]">Passed</Badge>
            )}
            {inspection.result === "failed" && (
              <Badge variant="outline" className="bg-destructive/15 text-destructive border-destructive/40 text-[10px]">Failed</Badge>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
            {inspection.scheduledDate && (
              <span className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />Scheduled {formatShortDate(inspection.scheduledDate)}
              </span>
            )}
            {inspection.completedDate && (
              <span className="flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3" />Completed {formatShortDate(inspection.completedDate)}
              </span>
            )}
            {inspection.assignedOwner && (
              <span className="flex items-center gap-1">
                <UserIcon className="h-3 w-3" />{inspection.assignedOwner}
              </span>
            )}
          </div>
          {inspection.result === "failed" && inspection.failureReason && (
            <p className="text-[11px] text-destructive">
              <span className="font-medium">Failure:</span> {inspection.failureReason}
            </p>
          )}
          {inspection.result === "failed" && (
            <div className="flex gap-3 text-[10px] text-muted-foreground">
              {inspection.correctionRequired && <span>· Correction required</span>}
              {inspection.reinspectionRequired && <span>· Reinspection required</span>}
            </div>
          )}
          {inspection.notes && (
            <p className="text-[11px] text-muted-foreground italic">{inspection.notes}</p>
          )}

          {editing && (
            <div className="pt-1">
              <ResultEntry
                onSubmit={(input) => { onRecord(jobId, inspection.id, input); setEditing(false); }}
                onCancel={() => setEditing(false)}
              />
            </div>
          )}
        </div>
        <div className="shrink-0">
          {!editing && !inspection.result && (
            <PermissionGate permission="jobs_approve">
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setEditing(true)}>
                Record result
              </Button>
            </PermissionGate>
          )}
          {!editing && inspection.result && (
            <PermissionGate permission="jobs_approve">
              <Button
                size="sm"
                variant="ghost"
                className="h-7 text-xs gap-1 text-muted-foreground"
                onClick={() => onClear(jobId, inspection.id)}
                title="Clear recorded result"
              >
                <RotateCcw className="h-3 w-3" /> Re-open
              </Button>
            </PermissionGate>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main panel ─────────────────────────────────────────────────────

export function PermitInspectionPanel({
  job,
  onAddInspection,
  onRecordResult,
  onClearResult,
}: Props) {
  const lifecycle = job.permitDetail?.inspectionLifecycle ?? null;
  const readiness = useMemo(() => resolveInspectionReadiness(lifecycle), [lifecycle]);

  const isApproved = job.permitDetail?.detailedStage === "Approved";
  const showPanel = isApproved || !!lifecycle;
  const [showAdd, setShowAdd] = useState(false);

  if (!showPanel) return null;

  // Approved but lifecycle not opened.
  if (!lifecycle) {
    return (
      <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
        <div className="flex items-center gap-2">
          <ClipboardCheck className="h-4 w-4 text-muted-foreground" />
          <h4 className="text-sm font-semibold text-foreground">Inspection Lifecycle</h4>
          <Badge variant="outline" className="bg-muted text-muted-foreground border-border">
            Not Started
          </Badge>
        </div>
        <p className="text-xs text-muted-foreground">
          Permit approved — inspections not started.
        </p>
        {showAdd ? (
          <AddInspectionForm
            onSubmit={(input) => { onAddInspection(job.id, input); setShowAdd(false); }}
            onCancel={() => setShowAdd(false)}
          />
        ) : (
          <PermissionGate permission="jobs_approve">
            <Button size="sm" onClick={() => setShowAdd(true)} className="h-7 text-xs gap-1">
              <Plus className="h-3 w-3" /> Add first inspection
            </Button>
          </PermissionGate>
        )}
      </div>
    );
  }

  const headerBadge =
    readiness.status === "complete"
      ? "bg-success/15 text-success border-success/30"
      : readiness.tone === "red"
        ? "bg-destructive/15 text-destructive border-destructive/40"
        : readiness.status === "active"
          ? "bg-warning/15 text-warning border-warning/30"
          : "bg-muted text-muted-foreground border-border";

  const headerLabel =
    readiness.status === "complete" ? "Complete" :
    readiness.status === "active" ? "Active" :
    "Not Started";

  const footerBadge =
    readiness.tone === "green" ? "bg-success/15 text-success border-success/30" :
    readiness.tone === "red" ? "bg-destructive/15 text-destructive border-destructive/40" :
    readiness.tone === "amber" ? "bg-warning/15 text-warning border-warning/30" :
    "bg-muted text-muted-foreground border-border";

  return (
    <div className="rounded-lg border p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center gap-2 flex-wrap">
        <ClipboardCheck className="h-4 w-4 text-muted-foreground" />
        <h4 className="text-sm font-semibold text-foreground">Inspection Lifecycle</h4>
        <Badge variant="outline" className={headerBadge}>{headerLabel}</Badge>
        {readiness.unresolvedFailures.length > 0 && (
          <span className="text-[11px] text-destructive flex items-center gap-1">
            <AlertTriangle className="h-3 w-3" />
            {readiness.unresolvedFailures.length} unresolved failure
            {readiness.unresolvedFailures.length === 1 ? "" : "s"}
          </span>
        )}
      </div>

      {/* Rows */}
      <div className="space-y-2">
        {lifecycle.inspections.length === 0 && (
          <p className="text-xs text-muted-foreground italic">No inspections logged</p>
        )}
        {lifecycle.inspections.map((ins) => (
          <InspectionRow
            key={ins.id}
            jobId={job.id}
            inspection={ins}
            onRecord={onRecordResult}
            onClear={onClearResult}
          />
        ))}
      </div>

      {/* Add */}
      {showAdd ? (
        <AddInspectionForm
          onSubmit={(input) => { onAddInspection(job.id, input); setShowAdd(false); }}
          onCancel={() => setShowAdd(false)}
        />
      ) : (
        <PermissionGate permission="jobs_approve">
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs gap-1"
            onClick={() => setShowAdd(true)}
          >
            <Plus className="h-3 w-3" /> Add inspection
          </Button>
        </PermissionGate>
      )}

      {/* Footer */}
      <div className="flex flex-wrap items-center justify-between gap-2 pt-3 border-t">
        <span className="text-xs tabular-nums text-muted-foreground">
          {readiness.progressLabel}
          {readiness.failedCount > 0 && (
            <span className="text-destructive ml-2">
              · {readiness.failedCount} failed
            </span>
          )}
        </span>
        <Badge variant="outline" className={footerBadge}>
          {readiness.badgeLabel}
        </Badge>
      </div>
    </div>
  );
}
